import { PNG_IMAGES, PngImageName } from '../../../../../shared/components/png-image/images';
import { ImageName, SVG_IMAGES } from '../../../../../shared/components/svg-image/images';
import { AvatarPlaceholder } from '../../../../../shared/helpers/avatar-placeholder';
import { getCssCustomProperty } from '../../../../../shared/helpers/get-css-custom-property';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { Participant } from '../../../types';

const DEFAULT_PNG_SIZE = 128;

type ImagesCacheItem = { elem: HTMLImageElement | undefined; isLoaded: boolean };

interface AvatarPlaceholderProperties {
    fontSize: number;
    placeholder: AvatarPlaceholder;
    size: number;
}

interface SvgProperties {
    backgroundColor?: string;
    iconColor?: string;
    name: ImageName;
    borderRadius?: number;
}

const logger = createLogger('draw-images');

const imagesCache = new Map<string | undefined, ImagesCacheItem>();

// Растёт при догрузке любой картинки. OverlayManager включает это значение в хэш, чтобы
// перерисовать overlay, когда асинхронно декодированная иконка/аватар наконец доступны.
// Сбрасывается в teardown рекордера вместе с остальными draw-кэшами.
let imagesLoadVersion = 0;

export const getImagesLoadVersion = () => imagesLoadVersion;

export const resetImagesLoadVersion = () => {
    imagesLoadVersion = 0;
};

const addImageToCache = (key: string, img: Maybe<HTMLImageElement>) => {
    const itemToCache: ImagesCacheItem = { elem: img, isLoaded: false };

    if (img) {
        img.onload = () => {
            itemToCache.isLoaded = true;
            imagesLoadVersion++;
        };

        img.onerror = () => {
            logger.warn('Load image for draw error', {
                method: 'addImageToCache',
                additional: { key },
            });
        };
    }

    imagesCache.set(key, itemToCache);
};

const getAvatarPlaceholderImageElement = ({
    placeholder,
    size,
    fontSize,
}: AvatarPlaceholderProperties): Maybe<HTMLImageElement> => {
    const { abbreviation, textColor, backgroundColor } = placeholder;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

    canvas.width = size;
    canvas.height = size;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const font = getCssCustomProperty('--typography-font-family');

    ctx.font = `${fontSize}px ${font}`;
    ctx.fillStyle = textColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(abbreviation, canvas.width / 2, canvas.height / 2 + Math.round(fontSize / 8));

    const img = document.createElement('img');
    img.src = canvas.toDataURL('image/png');

    return img;
};

const getSvgImageElement = ({
    backgroundColor,
    iconColor,
    name,
    borderRadius,
}: SvgProperties): Maybe<HTMLImageElement> => {
    let svgImage = SVG_IMAGES[name];

    if (iconColor) {
        svgImage = svgImage.replace(/(stroke|fill)="currentColor"/g, `$1="${iconColor}"`);
    }

    if (backgroundColor) {
        svgImage = svgImage.replace(
            /(<svg[^>]*>)/,
            `$1<rect width="100%" height="100%" fill="${backgroundColor}" rx="${borderRadius ?? 0}" />`
        );
    }

    const svgImageBase64 = btoa(svgImage);
    const image64 = `data:image/svg+xml;base64,${svgImageBase64}`;

    const img = document.createElement('img');
    img.src = image64;

    return img;
};

/** Возвращает закешированное изображение иконки */
export const getIconImage = (properties: SvgProperties) => {
    /** Генерируем ключ для хранения в кэше */
    const key = `${properties.name}|${properties.iconColor}|${properties.backgroundColor}`;

    /** Если находим запись в кэше, то работаем только с ней */
    const cachedImageItem = imagesCache.get(key);
    if (cachedImageItem) {
        return cachedImageItem.isLoaded ? cachedImageItem.elem : undefined;
    }

    /** Генерируем изображение, сохраняем в кэш и возвращаем его */
    const img = getSvgImageElement(properties);

    addImageToCache(key, img);

    return img;
};

/** Возвращает закешированное изображение аватарки гостя */
export const getAvatarGuestImage = ({ textColor: iconColor, backgroundColor }: AvatarPlaceholder) => {
    /** Генерируем ключ для хранения в кэше */
    const key = `guest|${iconColor}|${backgroundColor}`;

    /** Если находим запись в кэше, то работаем только с ней */
    const cachedImageItem = imagesCache.get(key);
    if (cachedImageItem) {
        return cachedImageItem.isLoaded ? cachedImageItem.elem : undefined;
    }

    /** Генерируем изображение, сохраняем в кэш и возвращаем его */
    const img = getSvgImageElement({
        name: 'guestIcon',
        iconColor,
        backgroundColor,
    });

    addImageToCache(key, img);

    return img;
};

/** Возвращает закешированное изображение инициал пользователя */
export const getAvatarPlaceholderImage = (properties: AvatarPlaceholderProperties) => {
    /** Генерируем ключ для хранения в кэше */
    const key =
        `${properties.placeholder.abbreviation}|` +
        `${properties.placeholder.textColor}|` +
        `${properties.placeholder.backgroundColor}|` +
        `${properties.fontSize}`;

    /** Если находим запись в кэше, то работаем только с ней */
    const cachedImageItem = imagesCache.get(key);
    if (cachedImageItem) {
        return cachedImageItem.isLoaded ? cachedImageItem.elem : undefined;
    }

    /** Генерируем изображение, сохраняем в кэш и возвращаем его */
    const img = getAvatarPlaceholderImageElement(properties);

    addImageToCache(key, img);

    return img;
};

/** Возвращает закешированное изображение аватарки пользователя */
export const getAvatarImage = (avatarUrl: Participant['avatarUrl'], imageSize: number): Maybe<HTMLImageElement> => {
    if (!avatarUrl) {
        return;
    }

    /** Генерируем ключ для хранения в кэше */
    const key = avatarUrl;

    /** Если находим запись в кэше, то работаем только с ней */
    const cachedImageItem = imagesCache.get(key);
    if (cachedImageItem) {
        return cachedImageItem.isLoaded ? cachedImageItem.elem : undefined;
    }

    /** Генерируем изображение, сохраняем в кэш и возвращаем его */
    const img = document.createElement('img');
    img.src = avatarUrl;
    img.crossOrigin = 'Anonymous';
    img.width = imageSize;
    img.height = imageSize;

    addImageToCache(key, img);

    return img;
};

export const getImage = (name: ImageName) => {
    const cachedImageItem = imagesCache.get(name);
    if (cachedImageItem) {
        return cachedImageItem.isLoaded ? cachedImageItem.elem : undefined;
    }
    const image = getSvgImageElement({ name });
    addImageToCache(name, image);
    return image;
};

export const getPngImage = (name: PngImageName, size: number = DEFAULT_PNG_SIZE) => {
    if (!name) {
        return;
    }

    const cachKey = `png-${name}`;
    const cachedImageItem = imagesCache.get(cachKey);

    if (cachedImageItem) {
        return cachedImageItem.isLoaded ? cachedImageItem.elem : undefined;
    }

    const image = document.createElement('img');

    image.src = PNG_IMAGES[name];
    image.crossOrigin = 'Anonymous';
    image.width = size;
    image.height = size;

    addImageToCache(cachKey, image);

    return image;
};
